import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useState } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import useAuth from "../../../hooks/useAuth";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import SearchableSelector from "../../SearchableSelector";
import { generateReqId } from "../../../utils/generateReqId";
import { generateUrlId } from "../../../utils/generateUrlID";

const PayWithAccount = ({ handleClose }) => {
  const { success, snackError, cookies } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const [options, setOptions] = useState([]);
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [isLoading, setIsLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleFormSubmit = async (values) => {
    setIsLoading(true);
    const { recipientAccountNumber, recipientAccountName, fromUser: fromAccountNo, amount, narration, makerReason } = values;
    const customerUserId = values.username?.userId || values.username?.id;
    const reference = "ADMIN_OTHER_" + Date.now();
    try {
      const response = await axiosPrivate.post(
        "/transaction-payment/api/v1/admin/maker-checker/maker/intra-bank-transfer",
        {
          userId: String(customerUserId),
          amount: Number(amount),
          fromAccountNumber: fromAccountNo,
          toAccountNumber: recipientAccountNumber,
          toAccountName: recipientAccountName,
          narration: narration || "Transfer to other Waya account",
          reference: reference,
          makerReason: makerReason,
          expiryHours: 24
        }
      );
      success(response.data?.message || "Transfer request submitted successfully (Maker initiated)!");
      if (handleClose) {
        handleClose();
      }
    } catch (error) {
      console.log(error);
      snackError(error?.response?.data?.message || "Failed to make transfer.");
    }
    setIsLoading(false);
  };

  const getWallets = async (userObj) => {
    const userId = userObj?.userId || userObj?.id;
    if (!userId) return;
    try {
      const response = await axiosPrivate.get(
        "/auth-serviceV3/api/v1/accounts/user/" + userId + "/details"
      );
      setOptions(response?.data?.data?.accounts || []);
    } catch (error) {
      console.log(error);
      snackError(error?.response?.data?.message || "Failed to fetch wallets.");
    }
  };

  const handleSelectChange = (e, newValue, setFieldValue) => {
    setFieldValue("username", newValue);
    getWallets(newValue);
  };

  const verifyAccount = async (accountNo, setFieldValue) => {
    if (!accountNo || accountNo.length !== 10) return;
    setVerifying(true);
    try {
      const res = await axiosPrivate.get(
        `/auth-serviceV3/api/v1/accounts/account/${accountNo}/enquiry`
      );
      const name = res?.data?.data?.accountName || res?.data?.accountName;
      if (name) {
        setFieldValue("recipientAccountName", name);
        success("Recipient account verified successfully!");
      } else {
        setFieldValue("recipientAccountName", "");
        snackError("Could not retrieve account name.");
      }
    } catch (error) {
      setFieldValue("recipientAccountName", "");
      snackError(error?.response?.data?.message || "Failed to verify account.");
    } finally {
      setVerifying(false);
    }
  };

  const initialValues = {
    username: null,
    recipientAccountNumber: "",
    recipientAccountName: "",
    amount: "",
    narration: "",
    makerReason: "",
    fromUser: "",
  };

  const checkoutSchema = yup.object().shape({
    username: yup.object().required("Required"),
    recipientAccountNumber: yup.number().required("Required"),
    recipientAccountName: yup.string().required("Required"),
    amount: yup.number().required("Required"),
    narration: yup.string(),
    makerReason: yup.string().required("Required"),
    fromUser: yup.string().required("Required"),
  });

  return (
    <Box width="100%">
      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={checkoutSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box padding={5}>
              <Typography
                variant="h3"
                sx={{ textAlign: "center", marginBottom: "30px" }}
              >
                Pay To Account
              </Typography>
              <Box
                display="grid"
                gap="20px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                  "& > div": {
                    gridColumn: isNonMobile ? undefined : "span 4",
                  },
                  marginBottom: "10px",
                }}
              >
                <SearchableSelector
                  value={values.username}
                  name="username"
                  sx={{ gridColumn: "span 2" }}
                  handleChange={(e, newValue) =>
                    handleSelectChange(e, newValue, setFieldValue)
                  }
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Recipient Account Number"
                  onBlur={handleBlur}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFieldValue("recipientAccountNumber", val);
                    if (val.length === 10) {
                      verifyAccount(val, setFieldValue);
                    }
                  }}
                  value={values.recipientAccountNumber}
                  name="recipientAccountNumber"
                  error={
                    !!touched.recipientAccountNumber &&
                    !!errors.recipientAccountNumber
                  }
                  helperText={
                    touched.recipientAccountNumber &&
                    errors.recipientAccountNumber
                  }
                  sx={{ gridColumn: "span 2" }}
                  InputProps={{
                    endAdornment: verifying ? (
                      <CircularProgress color="secondary" size={20} />
                    ) : null,
                  }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Recipient Account Name"
                  onBlur={handleBlur}
                  value={values.recipientAccountName}
                  name="recipientAccountName"
                  disabled
                  error={
                    !!touched.recipientAccountName &&
                    !!errors.recipientAccountName
                  }
                  helperText={
                    touched.recipientAccountName && errors.recipientAccountName
                  }
                  sx={{ gridColumn: "span 2" }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Amount"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.amount}
                  name="amount"
                  error={!!touched.amount && !!errors.amount}
                  helperText={touched.amount && errors.amount}
                  sx={{ gridColumn: "span 2" }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Add a note"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.narration}
                  name="narration"
                  error={!!touched.narration && !!errors.narration}
                  helperText={touched.narration && errors.narration}
                  sx={{ gridColumn: "span 2" }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Maker Reason"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.makerReason}
                  name="makerReason"
                  error={!!touched.makerReason && !!errors.makerReason}
                  helperText={touched.makerReason && errors.makerReason}
                  sx={{ gridColumn: "span 2" }}
                />
                <TextField
                  label="Transfer From"
                  fullWidth
                  select
                  name="fromUser"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.fromUser}
                  sx={{ gridColumn: "span 4" }}
                >
                  {options?.map((option, index) => {
                    const acctName = option.accountName || option.acct_name || "";
                    const accNo = option.accountNumber || option.accountNo || "";
                    return (
                      <MenuItem key={index} value={accNo}>
                        {acctName + " - " + accNo}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Box>
              <Button
                type="submit"
                color="secondary"
                disabled={isLoading || verifying}
                variant="contained"
                sx={{ width: "100%", p: 2, mt: 2 }}
              >
                Proceed
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default PayWithAccount;
